/**
 * Auth API
 * Authentication-related API endpoints
 */

import apiClient from './client';
import type {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  TwoFactorVerifyRequest,
  TwoFactorVerifyResponse,
  TwoFactorSetupResponse,
  ChangePasswordRequest,
  User,
} from '@/types/auth';

/**
 * Login with username and password
 */
export async function login(data: LoginRequest): Promise<LoginResponse> {
  const response = await apiClient.post<LoginResponse>('/auth/login', data);
  return response.data;
}

/**
 * Register new user
 */
export async function register(data: RegisterRequest): Promise<RegisterResponse> {
  const response = await apiClient.post<RegisterResponse>('/auth/register', data);
  return response.data;
}

/**
 * Verify 2FA code
 */
export async function verifyTwoFactor(
  data: TwoFactorVerifyRequest
): Promise<TwoFactorVerifyResponse> {
  const response = await apiClient.post<TwoFactorVerifyResponse>('/auth/2fa/verify', data);
  return response.data;
}

/**
 * Get current user profile
 */
export async function getProfile(): Promise<User> {
  const response = await apiClient.get<User>('/auth/profile');
  return response.data;
}

/**
 * Logout current user
 */
export async function logout(): Promise<void> {
  await apiClient.post('/auth/logout');
}

/**
 * Setup 2FA for current user
 */
export async function setupTwoFactor(): Promise<TwoFactorSetupResponse> {
  const response = await apiClient.post<TwoFactorSetupResponse>('/auth/2fa/setup');
  return response.data;
}

/**
 * Enable 2FA after verifying setup code
 */
export async function enableTwoFactor(code: string): Promise<{ backupCodes: string[] }> {
  const response = await apiClient.post<{ backupCodes: string[] }>('/auth/2fa/enable', { code });
  return response.data;
}

/**
 * Disable 2FA
 */
export async function disableTwoFactor(password: string): Promise<void> {
  await apiClient.post('/auth/2fa/disable', { password });
}

/**
 * Change password
 */
export async function changePassword(data: ChangePasswordRequest): Promise<void> {
  await apiClient.post('/auth/password/change', data);
}

/**
 * Request password reset
 */
export async function requestPasswordReset(email: string): Promise<void> {
  await apiClient.post('/auth/password/reset-request', { email });
}

/**
 * Reset password with token
 */
export async function resetPassword(token: string, newPassword: string): Promise<void> {
  await apiClient.post('/auth/password/reset', { token, newPassword });
}
